import React, { useState, useEffect, useCallback } from 'react';
import useGameStore from '../engine/store';
import { formatPrice } from '../engine/scenarios';

export default function ActionPanel() {
  const {
    playerCanAct, roundProcessing, scenario, currentOffer, opponentLastOffer,
    playerRole, submitOffer, doBluff, doPressure, doHold, doWalkAway, acceptOffer,
    playerBluffCount, pressuresUsed, tensionLevel,
  } = useGameStore();

  // Offer slider state
  const isBuyer = playerRole === 'buyer';
  const target = scenario?.playerTarget || 0;
  const list = scenario?.listPrice || 0;
  const opp = opponentLastOffer || list;

  const [offerValue, setOfferValue] = useState(isBuyer ? target : target);

  useEffect(() => {
    if (opp) {
      // Set slider to a reasonable counter
      if (isBuyer) {
        setOfferValue(Math.round((opp + target) / 2));
      } else {
        setOfferValue(Math.round((opp + target) / 2));
      }
    }
  }, [opp, target, isBuyer]);

  // Slider range
  const sliderMin = isBuyer
    ? Math.min(target * 0.7, scenario?.opponentFloor * 0.8 || target * 0.7)
    : Math.min(list * 0.8, opp);
  const sliderMax = isBuyer
    ? Math.max(list * 1.1, opp * 1.1)
    : Math.max(target * 1.3, scenario?.opponentFloor * 1.2 || target * 1.3);

  const step = Math.abs(list) >= 1e6 ? 50000
    : Math.abs(list) >= 10000 ? 500
    : Math.abs(list) >= 100 ? 5
    : 1;

  // Keyboard shortcuts
  const handleKey = useCallback((e) => {
    if (!playerCanAct || roundProcessing) return;
    if (e.target.closest('input')) return;

    switch (e.key) {
      case '1': submitOffer(offerValue); break;
      case '2': acceptOffer(); break;
      case '3': doHold(); break;
      case '4': doBluff(); break;
      case '5': doPressure(); break;
      case '6': doWalkAway(); break;
      default: break;
    }
  }, [playerCanAct, roundProcessing, offerValue, submitOffer, acceptOffer, doHold, doBluff, doPressure, doWalkAway]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const disabled = !playerCanAct || roundProcessing;

  // How good is current offer position?
  let offerQuality;
  if (isBuyer) {
    offerQuality = offerValue <= target ? 'great' : offerValue <= opp * 0.95 ? 'good' : 'fair';
  } else {
    offerQuality = offerValue >= target ? 'great' : offerValue >= opp * 1.05 ? 'good' : 'fair';
  }
  const qualityColors = { great: '#00ff88', good: '#ffcc00', fair: '#ff8800' };

  return (
    <div className="border-t border-white/5 bg-dark-800/80 backdrop-blur-md p-4">
      {/* Offer slider section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500 uppercase tracking-wider">Your Offer</span>
          <span className="text-xl font-black font-mono" style={{ color: qualityColors[offerQuality] }}>
            {formatPrice(offerValue, scenario)}
          </span>
        </div>

        {/* Context markers */}
        <div className="relative mb-1">
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            step={step}
            value={offerValue}
            onChange={(e) => setOfferValue(Number(e.target.value))}
            disabled={disabled}
            className="slider-custom w-full"
            style={{
              background: `linear-gradient(to right, #00ff88, #ffcc00, #ff3366)`,
              opacity: disabled ? 0.4 : 1,
            }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-600">
          <span>Your target: {formatPrice(target, scenario)}</span>
          <span>Their position: {formatPrice(opp, scenario)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {/* Submit offer */}
        <button
          onClick={() => submitOffer(offerValue)}
          disabled={disabled}
          className={`col-span-1 py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-neon-blue/15 border-neon-blue/30 text-neon-blue hover:bg-neon-blue/25'
          }`}
        >
          <div className="text-lg mb-0.5">💬</div>
          <div className="text-[10px]">Offer</div>
          <div className="text-[9px] text-gray-600">1</div>
        </button>

        {/* Accept their offer */}
        <button
          onClick={acceptOffer}
          disabled={disabled}
          className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-neon-green/15 border-neon-green/30 text-neon-green hover:bg-neon-green/25'
          }`}
        >
          <div className="text-lg mb-0.5">🤝</div>
          <div className="text-[10px]">Accept</div>
          <div className="text-[9px] text-gray-600">2</div>
        </button>

        {/* Hold */}
        <button
          onClick={doHold}
          disabled={disabled}
          className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-neon-yellow/15 border-neon-yellow/30 text-neon-yellow hover:bg-neon-yellow/25'
          }`}
        >
          <div className="text-lg mb-0.5">✋</div>
          <div className="text-[10px]">Hold</div>
          <div className="text-[9px] text-gray-600">3</div>
        </button>

        {/* Bluff */}
        <button
          onClick={doBluff}
          disabled={disabled}
          className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : playerBluffCount >= 3
                ? 'bg-neon-orange/10 border-neon-orange/20 text-neon-orange/60'
                : 'bg-neon-orange/15 border-neon-orange/30 text-neon-orange hover:bg-neon-orange/25'
          }`}
        >
          <div className="text-lg mb-0.5">🃏</div>
          <div className="text-[10px]">Bluff</div>
          <div className="text-[9px] text-gray-600">4 {playerBluffCount > 0 ? `(${playerBluffCount}x)` : ''}</div>
        </button>

        {/* Pressure */}
        <button
          onClick={doPressure}
          disabled={disabled}
          className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-neon-red/15 border-neon-red/30 text-neon-red hover:bg-neon-red/25'
          }`}
        >
          <div className="text-lg mb-0.5">⚡</div>
          <div className="text-[10px]">Pressure</div>
          <div className="text-[9px] text-gray-600">5</div>
        </button>

        {/* Walk away */}
        <button
          onClick={doWalkAway}
          disabled={disabled}
          className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 border ${
            disabled
              ? 'bg-dark-600 border-white/5 text-gray-600 cursor-not-allowed'
              : 'bg-dark-600 border-white/10 text-gray-400 hover:bg-neon-red/10 hover:border-neon-red/30 hover:text-neon-red'
          }`}
        >
          <div className="text-lg mb-0.5">🚶</div>
          <div className="text-[10px]">Walk</div>
          <div className="text-[9px] text-gray-600">6</div>
        </button>
      </div>
    </div>
  );
}
